import { useEffect, useMemo, useState, useCallback } from "react";

import { useQuery } from "@apollo/client";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { interval } from "rxjs";
import { distinctUntilChanged, startWith } from "rxjs/operators";

import {
    RVPaddedPage,
    defaultQueryParams,
    useQueryParamsV6,
    hasMorePagination,
    FilterParams,
    useSearchableColumns,
    RVLoadingGrid as LoadingGrid,
    usePageConfig,
} from "@regal-voice/ui";
import { ORCHESTRATION_SUB_NAV_TITLES } from "Pages/orchestration/orchestrationMetaData";
import { orchestrationPathResources } from "Pages/orchestration/Pages/orchestrationPaths";
import { getUserCountsByJourneysIds, UserCountsByJourney } from "Services/Journeys.service";
import { renderErrorMessage } from "Services/LoggingService";
import { getJourneys, getJourneysAgents } from "Services/marketing-api/journeys/queries";
import { getTags } from "Services/marketing-api/tags/queries";
import { Journey, JourneyWithMetrics } from "Types/Journey";

import JourneysHeader from "./JourneysHeader/JourneysHeader";
import { columns } from "./JourneyTable/columns";

interface GetJourneysResult {
    items: Array<JourneyWithMetrics>;
    hasMore?: boolean;
}
const SEARCHABLE_FIELDS = ["search"];
const SEARCHABLE_POLL_INTERVAL = 15000;

export function JourneysIndex(): JSX.Element {
    usePageConfig({ pageTitle: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.journeys] });

    const navigate = useNavigate();

    const [userCounts, setUserCounts] = useState<{ [uuid: string]: UserCountsByJourney }>({});
    const { queryParams } = useQueryParamsV6(defaultQueryParams);
    const filterValue = queryParams.filterBy?.find((value) => value.key === "search");
    const defaultSearch = filterValue?.value as string | undefined;

    const { loading, columnsData, setFilterValue } = useSearchableColumns<JourneyWithMetrics>({
        query: getJourneys,
        defaultParams: defaultQueryParams,
        queryName: "getJourneys",
        searchableFields: SEARCHABLE_FIELDS,
        fetchPolicy: "cache-and-network",
        pollInterval: SEARCHABLE_POLL_INTERVAL,
    });

    const { data } = useQuery(getJourneysAgents);
    const { data: tagsData } = useQuery(getTags, {
        onError: (error) => renderErrorMessage({ content: `Error loading tags ${error.message}.`, error }),
    });

    const { journeysResult, journeysIds } = useMemo<{ journeysResult: GetJourneysResult; journeysIds: string }>(() => {
        return {
            journeysResult: {
                items: columnsData.items.map((cd) => ({
                    ...cd,
                    journeyStats: userCounts[cd.id] || { loading: true },
                })),
                hasMore: columnsData.hasMore,
            },
            journeysIds: columnsData.items.map((journey) => journey.id).join(","),
        };
    }, [columnsData.hasMore, columnsData.items, userCounts]);

    useEffect(() => {
        if (!journeysIds?.length) {
            return;
        }

        const subscriber = interval(15000)
            .pipe(startWith(0), distinctUntilChanged())
            .subscribe(() =>
                getUserCountsByJourneysIds({ journeysIds: journeysIds.split(",") })
                    .then(({ journeyPageCounts }) => {
                        const countsByJourneyUuid = Object.fromEntries(
                            journeyPageCounts.map((journeyCounts) => {
                                return [journeyCounts.journeyUuid, journeyCounts];
                            })
                        );
                        setUserCounts(countsByJourneyUuid);
                    })
                    .catch(() => message.error("Journeys stats could not be loaded"))
            );

        return () => subscriber.unsubscribe();
    }, [journeysIds]);

    const filtersCount =
        queryParams.filterBy?.filter((i: FilterParams) => !SEARCHABLE_FIELDS.includes(i.key))?.length || 0;

    const linkToJourneyBuilder = useCallback(() => {
        navigate("new");
    }, [navigate]);

    // TODO(RD-1486): make GridService#userColumnFilters work for this instead
    const filteredColumns = useMemo(() => {
        const filters = {
            updatedByNames: data?.getJourneysAgents?.updatedByNames || [],
            createdByNames: data?.getJourneysAgents?.createdByNames || [],
        };

        return columns({
            queryParams,
            filterItems: filters,
            tags: tagsData?.getTags?.items || [], // Tags are sorted alphabetically server side
        });
    }, [data?.getJourneysAgents?.updatedByNames, data?.getJourneysAgents?.createdByNames, queryParams, tagsData]);

    return (
        <RVPaddedPage>
            <LoadingGrid
                defaultParams={defaultQueryParams}
                additionalFilters={SEARCHABLE_FIELDS}
                totalRecords={hasMorePagination(journeysResult.items, queryParams, journeysResult.hasMore)}
                loading={loading}
                rowKey={(record: Journey) => `${record.id}-${record.updatedAt}`}
                flexibleHeader={
                    <JourneysHeader
                        linkToJourneyBuilder={linkToJourneyBuilder}
                        defaultSearch={defaultSearch || ""}
                        filtersCount={filtersCount}
                        setFilterValue={setFilterValue}
                        baseParams={defaultQueryParams}
                    />
                }
                skeletonRowLines={3}
                columns={filteredColumns}
                columnsData={journeysResult.items}
                pageWidth="wide"
                headerSpacing="small"
            />
        </RVPaddedPage>
    );
}
